import React, { createContext, useContext, useCallback, useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface TierConfig {
  tier: string;
  tier_order: number;
  display_name: string;
  emoji: string;
  min_points: number;
}

interface GamificationContextType {
  addPoints: (actionKey: string, customDescription?: string) => Promise<void>;
  totalPoints: number;
  currentTier: string;
  currentDivision: number;
}

const GamificationContext = createContext<GamificationContextType | null>(null);

export const useGamificationContext = () => {
  const context = useContext(GamificationContext);
  if (!context) {
    throw new Error('useGamificationContext must be used within a GamificationProvider');
  }
  return context;
};

export const GamificationProvider = ({ children }: { children: React.ReactNode }) => {
  const [userId, setUserId] = useState<string | null>(null);
  const [totalPoints, setTotalPoints] = useState(0);
  const [currentTier, setCurrentTier] = useState('sedentario_arrependido');
  const [currentDivision, setCurrentDivision] = useState(4);
  const [tiers, setTiers] = useState<TierConfig[]>([]);

  useEffect(() => {
    const loadUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUserId(session.user.id);
        
        // Load tiers
        const { data: tierData } = await supabase
          .from('gamification_tier_config')
          .select('tier, tier_order, display_name, emoji, min_points')
          .order('tier_order', { ascending: true });
        
        if (tierData) setTiers(tierData);

        // Load user gamification
        const { data: gamData } = await supabase
          .from('user_gamification')
          .select('total_points, current_tier, current_division')
          .eq('user_id', session.user.id)
          .maybeSingle();

        if (gamData) {
          setTotalPoints(gamData.total_points);
          setCurrentTier(gamData.current_tier);
          setCurrentDivision(gamData.current_division);
        }
      }
    };
    loadUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        setUserId(session.user.id);
      } else {
        setUserId(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const addPoints = useCallback(async (actionKey: string, customDescription?: string) => {
    if (!userId || tiers.length === 0) return;

    // Call the secure server-side function
    const { data, error } = await supabase.rpc('award_gamification_points', {
      p_action_key: actionKey,
      p_description: customDescription || null,
    });

    if (error) {
      console.error('Error awarding points:', error);
      return;
    }

    const result = data as { success: boolean; reason?: string; points_earned?: number; total_points?: number; current_tier?: string; current_division?: number };

    if (!result?.success) {
      // Silently skip - already awarded or invalid action
      return;
    }

    const tierChanged = result.current_tier !== currentTier;
    const divisionChanged = result.current_division !== currentDivision;

    // Update local state
    setTotalPoints(result.total_points!);
    setCurrentTier(result.current_tier!);
    setCurrentDivision(result.current_division!);

    // Show toast
    if (tierChanged) {
      const newTierConfig = tiers.find(t => t.tier === result.current_tier);
      toast.success(`🎉 Você subiu para ${newTierConfig?.emoji} ${newTierConfig?.display_name}!`);
    } else if (divisionChanged && result.current_division! < currentDivision) {
      toast.success(`⬆️ Você subiu para Divisão ${result.current_division}!`);
    } else {
      toast.success(`+${result.points_earned} pontos!`, { duration: 1500 });
    }
  }, [userId, currentTier, currentDivision, tiers]);

  const contextValue = useMemo(() => ({
    addPoints, totalPoints, currentTier, currentDivision
  }), [addPoints, totalPoints, currentTier, currentDivision]);

  return (
    <GamificationContext.Provider value={contextValue}>
      {children}
    </GamificationContext.Provider>
  );
};
